// Inviter — lag, vis og tilbakekall registreringslenker.
//
// FIRE TING SOM IKKE ER FORHANDLINGSBARE PÅ DENNE SKJERMEN:
//
//  1. LENKEN VISES ÉN GANG. `POST /lenker` er eneste sted koden finnes i
//     klartekst; lageret har bare hash + oppslagsprefiks. Derfor er
//     engangsvisningen det første på skjermen, den sier med ord at koden ikke kan
//     hentes fram igjen, og «Lukk» er sperret til operatøren har krysset av for at
//     hun har tatt vare på lenken. En tapt lenke er ikke reparerbar — den må
//     erstattes av en ny, og den gamle bør tilbakekalles.
//
//  2. UTEN ATTESTASJONSRETT VISES INGEN SKJEMA. `POST /lenker` svarer 403 uten
//     `kanAttestere`, og et skjema som alltid ender i 403 lærer brukeren at
//     verktøyet er upålitelig. Vi sjekker `kanAttestere` fra `GET /meg` og
//     forklarer i stedet hva hun skal gjøre. Mister hun retten MENS skjermen står
//     åpen, forsvinner skjemaet også da — se `hent()`.
//
//  3. GYLDIGHET KOMMER FRA TJENESTEN. `gyldig` og `grunn` på hver rad er regnet
//     av `kontrollerLenke()` server-side og vises ordrett. Klienten navngir bare
//     hvilken av grunnene det er, for merkelappen (lib/lenke-lib.ts).
//
//  4. TILBAKEKALL SKJER PÅ PREFIKSET. Vi har ikke koden — den finnes ikke lenger.
//     `DELETE /lenker/:id` godtar prefikset (≥ 4 tegn), og 409 betyr at seks tegn
//     ikke pekte på én lenke. Da sier vi det rett ut framfor å gjette, for å
//     tilbakekalle feil lenke kan ikke angres.
import type { Kontekst } from './kontekst.js';
import {
  annetOpphav, bruktTekst, lenkeKropp, lenketilstand, lesLenker, lesNyLenke, levetidMsAv,
  prefiksVisning, slagTekst, standardUtkast, utløpstekst, validerNyLenke,
  LEVETIDSVALG, MAKS_BRUK, STANDARD_LEVETID_DØGN, TILSTAND_MERKE, TILSTAND_VARIANT,
  type LenkeRad, type LenkeUtkast, type NyLenke,
} from '../lib/lenke-lib.js';
import { formaterDatoTid } from '../lib/tid.js';
import {
  avkrysning, div, felt, h, knapp, kopierknapp, merke, nv, p, sett, span, velger,
} from '../ui/dom.js';
import { ikon } from '../ui/ikoner.js';
import { feilboks, laster, tomt } from '../ui/tilstand.js';

export function visInviter(rot: HTMLElement, ctx: Kontekst): void {
  const opphav = globalThis.location?.origin ?? '';
  let rader: LenkeRad[] = [];
  /** Sann mens en fersk lenke står på skjermen og ennå ikke er kvittert ut. */
  let engangsVises = false;

  const side = div('side');
  sett(rot, side);

  const topp = div('side-topp');
  const titler = div('');
  titler.append(h(1, 'side-tittel', 'Inviter'));
  titler.append(p('side-under', 'Lag en registreringslenke og send den til den som skal føre timer. '
    + 'Hun registrerer seg selv, får eget pseudonym og token, og du ser aldri ansattnummeret hennes.'));
  topp.append(titler);
  side.append(topp);

  // Uten attestasjonsrett stopper skjermen her. Ingen skjema, ingen liste — de to
  // kallene ville begge svart 403.
  if (!ctx.meg.person.kanAttestere) {
    side.append(utenRett());
    return;
  }

  const oppdater = knapp('Oppdater', () => void hent(), { variant: 'stille', ikon: ikon('last') });
  const toppknapper = div('filterrad');
  toppknapper.append(oppdater);
  topp.append(toppknapper);

  const engangsplass = div('engangsplass');
  side.append(engangsplass);

  const skjemaplass = div('');
  side.append(skjemaplass);

  const listeplass = div('');
  side.append(listeplass);

  // Vakt mot å navigere bort fra en lenke som bare finnes på skjermen. Den
  // fanger klikk i sidenavigasjonen; nettleserens tilbakeknapp og en manuell
  // adresseendring gjør den ingenting med — derfor står advarselen og
  // kopier-knappen der uansett, framfor å hvile på vakten alene.
  ctx.settNavigasjonsvakt(() => {
    if (!engangsVises) return true;
    return globalThis.confirm(
      'Invitasjonslenken vises bare nå og kan ikke hentes fram igjen. '
      + 'Forlater du skjermen uten å ha kopiert den, må du lage en ny. Gå videre likevel?',
    );
  });

  const skjema = byggSkjema();
  void hent();

  // ══ Uten rettighet ═════════════════════════════════════════════════════
  /**
   * Den rolige forklaringen. Dette er hele forskjellen mellom en ærlig og en
   * frustrerende skjerm: retten kan ikke gis herfra, så vi sier hvem som kan gi
   * den og hva man gjør i mellomtiden.
   */
  function utenRett(fraTjenesten?: string): HTMLElement {
    const kort = div('kort');
    const hode = div('kort-hode');
    hode.append(ikon('info'), h(2, 'kort-tittel', 'Du kan ikke lage invitasjonslenker'));
    kort.append(hode);

    if (fraTjenesten) {
      // Tjenestens egen 403-tekst sier hva man skal gjøre. Den vises ordrett.
      kort.append(p('kort-brød', fraTjenesten));
    }
    kort.append(p('kort-brød', 'Å invitere krever attestasjonsrett — samme rett som lar deg '
      + 'godkjenne andres timer. Brukeren din har den ikke, og den kan ikke gis fra denne '
      + 'skjermen: rettigheten settes av drift på personen i dugnad-tjenesten.'));
    kort.append(p('kort-brød', 'Slik kommer du videre: be en i avdelingen som har attestasjonsrett '
      + 'om å lage lenken og sende den til deg, eller be drift om å gi deg retten. Timeføring, '
      + 'kanban og sprinter virker som før uten den.'));
    kort.append(nv('Pseudonymet ditt', ctx.meg.person.id));
    // «nei» også når `/meg` fortsatt sier ja: kommer denne teksten av en 403, er
    // det tjenesten som har svart, og tjenesten er den som avgjør (VALE §4).
    kort.append(nv('Attestasjonsrett', fraTjenesten ? 'nei — tjenesten avviste kallet' : 'nei'));
    return kort;
  }

  // ══ Skjemaet ═══════════════════════════════════════════════════════════
  function byggSkjema() {
    const kort = div('kort kort-skjema');
    sett(skjemaplass, kort);

    const hode = div('kort-hode');
    hode.append(ikon('inviter'), h(2, 'kort-tittel', 'Lag invitasjonslenke'));
    kort.append(hode);
    kort.append(p('kort-brød', 'Standarden er én bruk og '
      + `${STANDARD_LEVETID_DØGN} døgns levetid — trykk «Lag lenke» hvis det passer. `
      + 'Lenken er en hemmelighet: den gir rett til å opprette en konto som kan føre timer, '
      + 'så den skal deles i en lukket kanal.'));

    const form = document.createElement('form');
    form.className = 'skjema-rutenett';
    form.noValidate = true;

    const start = standardUtkast();
    const antall = felt('Antall bruk', {
      type: 'number',
      verdi: String(start.maksBruk),
      inndatamodus: 'numeric',
      hjelp: '1 = personlig invitasjon til én person. Flere = områdelenke som kan deles med et lag.',
    });
    antall.input.min = '1';
    antall.input.max = String(MAKS_BRUK);
    antall.input.step = '1';

    const levetid = velger('Levetid', LEVETIDSVALG, String(STANDARD_LEVETID_DØGN));
    const tilhørighet = felt('Tilhørighet', {
      plassholder: 'valgfritt',
      hjelp: 'Settes på den som registrerer seg, så hun ikke må skrive den selv.',
    });
    const merknad = felt('Merknad', {
      plassholder: 'valgfritt',
      hjelp: 'Vises til den som åpner lenken. Én linje om hva dette gjelder.',
    });

    const slag = span('varighet-hint', slagTekst(start.maksBruk));
    const oppdaterSlag = (): void => {
      slag.textContent = slagTekst(Number(antall.input.value));
    };
    antall.input.addEventListener('input', oppdaterSlag);

    const lag = knapp('Lag lenke', undefined, { variant: 'primær', type: 'submit', ikon: ikon('pluss') });
    const skjemafeil = div('skjema-feil');
    skjemafeil.setAttribute('role', 'alert');
    skjemafeil.hidden = true;

    form.append(antall.rot, levetid.rot);
    const bred = div('skjema-bred');
    bred.append(tilhørighet.rot);
    form.append(bred);
    const bred2 = div('skjema-bred');
    bred2.append(merknad.rot);
    form.append(bred2);
    const fot = div('skjema-fot');
    fot.append(slag, lag);
    form.append(fot);
    form.append(skjemafeil);
    kort.append(form);

    form.addEventListener('submit', (e) => { e.preventDefault(); void send(); });

    async function send(): Promise<void> {
      for (const f of [antall, tilhørighet, merknad]) f.settFeil(null);
      skjemafeil.hidden = true;

      const utkast: LenkeUtkast = {
        maksBruk: Number(antall.input.value.trim()),
        tilhorighet: tilhørighet.input.value,
        merknad: merknad.input.value,
        levetidMs: levetidMsAv(levetid.select.value),
      };
      const feil = validerNyLenke(utkast);
      if (feil) {
        const mål = { maksBruk: antall, tilhorighet: tilhørighet, merknad }[
          feil.felt as 'maksBruk' | 'tilhorighet' | 'merknad'
        ];
        if (mål) {
          mål.settFeil(feil.grunn);
          mål.input.focus();
        } else {
          skjemafeil.hidden = false;
          skjemafeil.textContent = feil.grunn;
        }
        return;
      }

      lag.disabled = true;
      const før = lag.textContent;
      lag.textContent = 'Lager …';
      const r = await ctx.klient.nyLenke(lenkeKropp(utkast));
      lag.disabled = false;
      lag.textContent = før;

      if (!r.ok) {
        if (r.feil.art === 'forbudt') return mistetRett(r.feil.melding);
        skjemafeil.hidden = false;
        skjemafeil.textContent = r.feil.melding;
        return;
      }

      const ny = lesNyLenke(r.data, opphav);
      if (!ny) {
        // Svaret manglet koden. Lenken KAN være opprettet — det ser vi i lista —
        // men den er i praksis tapt, og da er tilbakekall det ryddige.
        skjemafeil.hidden = false;
        skjemafeil.textContent = 'Tjenesten svarte uten lenkekode. Lenken kan likevel være '
          + 'opprettet: se lista under. Står den der, er koden tapt — tilbakekall den og lag en ny.';
        await hent();
        return;
      }
      // Ingen `kunngjør` her: panelet under har `role=alert` og leses opp i sin
      // helhet. To samtidige kunngjøringer konkurrerer, og da leses enten alt
      // eller ingenting (se ui/tilstand.ts).
      visEngangs(ny);
      await hent();
    }

    return { fokuser: () => antall.input.focus() };
  }

  // ══ Engangsvisningen ═══════════════════════════════════════════════════
  function visEngangs(ny: NyLenke): void {
    engangsVises = true;
    const boks = div('advarsel engangs');
    boks.setAttribute('role', 'alert');

    const hode = div('advarsel-hode');
    hode.append(ikon('varsel'));
    hode.append(h(2, 'advarsel-tittel', 'Lenken vises bare nå'));
    boks.append(hode);
    boks.append(p('advarsel-linje', 'Tjenesten har bare en hash av denne koden og kan ikke '
      + 'vise den igjen. Kopiér lenken nå og send den i en lukket kanal. Lukker du dette uten '
      + 'å ha tatt vare på den, er lenken tapt — da må du tilbakekalle den og lage en ny.'));

    const rad = div('hemmelighet');
    rad.append(span('hemmelighet-etikett', 'Invitasjonslenke'));
    const kode = document.createElement('code');
    kode.className = 'kodeverdi';
    kode.textContent = ny.url;
    // Merkbar med tastatur, så den kan kopieres for hånd om utklippstavla er av.
    kode.tabIndex = 0;
    rad.append(kode);
    const kopier = kopierknapp(ny.url, 'Invitasjonslenken', ctx.kvitter);
    rad.append(kopier);
    boks.append(rad);

    if (!ny.urlFraTjenesten) {
      boks.append(p('advarsel-linje', 'Tjenesten sendte ingen ferdig adresse, så denne er satt '
        + `sammen av adressen i nettleseren (${opphav}). Kontrollér at den stemmer før du deler den.`));
    } else if (annetOpphav(ny.url, opphav)) {
      boks.append(p('advarsel-linje', 'Merk: lenken peker på et annet vertsnavn enn siden du står '
        + 'på. Det er drift som setter det (DUGNAD_BASIS_URL) — er det feil, virker ikke lenken.'));
    }

    boks.append(nv('Type', slagTekst(ny.maksBruk)));
    boks.append(nv('Utløper', `${formaterDatoTid(ny.utloperTsMs)} (${utløpstekst(ny.utloperTsMs, Date.now())})`));
    if (ny.tilhorighet) boks.append(nv('Tilhørighet', ny.tilhorighet));
    if (ny.merknad) boks.append(nv('Merknad', ny.merknad));
    boks.append(nv('Kjennes igjen i lista som', prefiksVisning(ny.idPrefiks)));

    // Lukkesteget krever et aktivt kryss. Grunnen er ikke formalisme: panelet
    // ligger over skjemaet, og et enkelt «Lukk» treffes lett med samme
    // museklikk som nettopp laget lenken.
    const kvittert = avkrysning('Jeg har kopiert lenken og lagret den et trygt sted', false);
    const lukk = knapp('Lukk', () => lukkEngangs(), { variant: 'stille', ikon: ikon('kryss') });
    lukk.disabled = true;
    kvittert.input.addEventListener('change', () => { lukk.disabled = !kvittert.input.checked; });

    boks.append(kvittert.rot);
    const fot = div('skjema-fot');
    fot.append(lukk);
    boks.append(fot);

    sett(engangsplass, boks);
    if (typeof boks.scrollIntoView === 'function') boks.scrollIntoView({ block: 'nearest' });
    kopier.focus();
  }

  function lukkEngangs(): void {
    engangsVises = false;
    sett(engangsplass);
    ctx.kunngjør('Engangsvisningen er lukket.');
    skjema.fokuser();
  }

  // ══ Lista ══════════════════════════════════════════════════════════════
  async function hent(): Promise<void> {
    sett(listeplass, laster('Henter utstedte lenker …', 3));
    oppdater.disabled = true;
    const r = await ctx.klient.lenker();
    oppdater.disabled = false;
    if (!r.ok) {
      // 403 her betyr at attestasjonsretten er trukket tilbake mens skjermen sto
      // åpen. Da skal skjemaet BORT, ikke stå og love noe tjenesten vil avvise.
      if (r.feil.art === 'forbudt') return mistetRett(r.feil.melding);
      return sett(listeplass, feilboks(r.feil, () => void hent()));
    }
    rader = lesLenker(r.data);
    tegnListe();
  }

  /**
   * Retten er borte. Rydd skjemaet og «Oppdater» — begge ville bare gitt 403 en
   * gang til — og hent `/meg` så navigasjonen slutter å vise «Inviter».
   *
   * En engangsvisning som alt står på skjermen får BLI. Lenken er opprettet, og
   * at retten forsvant etterpå gjør den ikke mindre verdt å kopiere.
   */
  function mistetRett(melding: string): void {
    oppdater.remove();
    sett(skjemaplass);
    sett(listeplass, utenRett(melding));
    void ctx.oppdaterMeg();
  }

  function tegnListe(): void {
    const kort = div('kort');
    const hode = div('kort-hode');
    hode.append(ikon('lenke'), h(2, 'kort-tittel', 'Utstedte lenker'));
    hode.append(merke(String(rader.length)));
    kort.append(hode);
    kort.append(p('kort-brød', 'Koden vises bare når lenken lages. Her står de seks første '
      + 'tegnene av den, så du kan kjenne igjen hvilken lenke det er — og tilbakekalle den. '
      + 'Å tilbakekalle stopper nye registreringer; de som alt har registrert seg beholder '
      + 'tilgangen sin.'));

    if (!rader.length) {
      kort.append(tomt('Ingen lenker er utstedt ennå',
        'Lag den første i skjemaet over. Standarden — én bruk, sju døgn — passer til å '
        + 'invitere én person.',
        { ikon: 'lenke' }));
    } else {
      for (const l of rader) kort.append(lenkerad(l));
    }
    sett(listeplass, kort);
  }

  function lenkerad(l: LenkeRad): HTMLElement {
    const nå = Date.now();
    const v = lenketilstand(l, nå);
    const rad = div(`lenkerad${v.gyldig ? '' : ' ubrukelig'}`);

    const tekst = div('lenkerad-tekst');
    tekst.append(span('lenkerad-prefiks', prefiksVisning(l.idPrefiks)));
    tekst.append(span('lenkerad-detalj',
      `${slagTekst(l.maksBruk)} · ${bruktTekst(l.brukt, l.maksBruk)}`));
    tekst.append(span('lenkerad-detalj',
      `Opprettet ${formaterDatoTid(l.opprettetTsMs)} av ${l.opprettetAv}`));
    tekst.append(span('lenkerad-detalj',
      `${utløpstekst(l.utloperTsMs, nå)} · ${formaterDatoTid(l.utloperTsMs)}`));
    if (l.tilbakekaltTsMs !== null) {
      tekst.append(span('lenkerad-detalj', `Tilbakekalt ${formaterDatoTid(l.tilbakekaltTsMs)}`));
    }
    if (l.merknad) tekst.append(span('lenkerad-merknad', l.merknad));
    // Tjenestens egen grunn, ordrett. Den er skrevet for et menneske.
    if (v.grunn) tekst.append(span('lenkerad-detalj', v.grunn));
    rad.append(tekst);

    const merker = div('lenkerad-merker');
    merker.append(merke(TILSTAND_MERKE[v.tilstand], TILSTAND_VARIANT[v.tilstand]));
    if (l.tilhorighet) merker.append(merke(l.tilhorighet));
    rad.append(merker);

    const feilEl = div('lenkerad-feil');
    feilEl.setAttribute('role', 'alert');
    feilEl.hidden = true;

    const handlinger = div('lenkerad-handlinger');
    if (l.tilbakekaltTsMs === null) {
      handlinger.append(knapp('Tilbakekall', () => void tilbakekall(l, feilEl),
        { variant: 'fare', ikon: ikon('avvis') }));
    }
    rad.append(handlinger);
    rad.append(feilEl);
    return rad;
  }

  async function tilbakekall(l: LenkeRad, feilEl: HTMLElement): Promise<void> {
    const bekreftet = globalThis.confirm(
      `Tilbakekalle lenken ${prefiksVisning(l.idPrefiks)}?\n\n`
      + 'Den slutter å virke med en gang, og det kan ikke angres. Har noen alt registrert seg '
      + 'med den, beholder de tilgangen sin — tilbakekallet stopper bare nye registreringer.',
    );
    if (!bekreftet) return;

    feilEl.hidden = true;
    const r = await ctx.klient.tilbakekallLenke(l.idPrefiks);
    if (!r.ok) {
      if (r.feil.art === 'forbudt') return mistetRett(r.feil.melding);
      feilEl.hidden = false;
      feilEl.textContent = r.feil.art === 'konflikt'
        // 409 = prefikset traff flere lenker. Hele koden ville løst det, men den
        // finnes ikke lenger noe sted — så dette må drift rydde i lageret.
        ? `${r.feil.melding} De seks tegnene vi har er ikke nok til å peke på én lenke, og hele `
          + 'koden finnes ikke lenger. Be drift tilbakekalle den med admin-token.'
        : r.feil.melding;
      return;
    }
    ctx.kvitter('Lenken er tilbakekalt');
    ctx.kunngjør(`Lenken ${prefiksVisning(l.idPrefiks)} er tilbakekalt.`);
    await hent();
  }
}
